/**
 * Locked pages are pages with fixed positions that maintain their page numbers
 * even when content changes. The plugin supports locking/unlocking individual
 * pages or all pages, setting custom page names (aliases), and managing empty
 * locked pages.
 *
 * There are currently two pagination plugins our editor supports. Commands here
 * route to whichever paginator is active, so callers don't need to be aware.
 */

import { chainCommands } from 'prosemirror-commands'
import { Node } from 'prosemirror-model'
import { type Command } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'

import { constants } from '@showrunner/prose-schemas'

import { isLockedPageBreak, LOCKED } from '#util/pagination/helpers'

import { getPaginationBreakdown } from './inlinePageBreaks'
import * as newCommands from './pagination/locked-page-commands'

export type PagePosition = [pos: number, pageNumber: string | number]

const isPage = (node: Node | null) =>
  node?.type.name === constants.NodeTypeMap.PAGE

const focus = (view: EditorView | undefined) => {
  if (!view) return
  requestAnimationFrame(() => {
    view.focus()
  })
}

/**
 * Commands
 */

const lockAllPagesLegacy: Command = (state, dispatch, view) => {
  const paginationBreakdown = getPaginationBreakdown(state)
  if (!paginationBreakdown) return false

  const pageNumbers = paginationBreakdown.decorationData.map(
    (page) => page.pageNumber,
  )
  return lockPagesLegacy(pageNumbers)(state, dispatch, view)
}

export const lockAllPages = chainCommands(
  newCommands.lockAllPages,
  lockAllPagesLegacy,
)

const unlockAllPagesLegacy: Command = (state, dispatch, view) => {
  const { tr } = state
  tr.doc.descendants((node, pos, parent) => {
    if (isPage(parent)) {
      const isLocked = isLockedPageBreak(node)
      if (isLocked) tr.setNodeAttribute(pos, 'pageBreak', null)
      return false // stop descending regardless
    }
  })
  if (tr.steps.length > 0) {
    if (dispatch) {
      dispatch(tr)
      focus(view)
    }
  }
  return tr.steps.length > 0
}

export const unlockAllPages = chainCommands(
  newCommands.unlockAllPages,
  unlockAllPagesLegacy,
)

/**
 * Commands with parameters
 */

export function lockPages(pageNumbers: number[]): Command {
  return chainCommands(
    newCommands.lockPages(pageNumbers),
    lockPagesLegacy(pageNumbers),
  )
}

function lockPagesLegacy(pageNumbers: number[]): Command {
  return (state, dispatch, view) => {
    const paginationBreakdown = getPaginationBreakdown(state)
    if (!paginationBreakdown) return false

    const pagesToLock: PagePosition[] = paginationBreakdown.decorationData
      .filter(
        (page) =>
          pageNumbers.includes(page.pageNumber) && !page.isLockedPageBreak,
      )
      .map((p) => [p.startPos, p.pageAlias ?? p.pageNumber])

    if (pagesToLock.length === 0) return false

    const { tr } = state
    pagesToLock.forEach(([pos, pageNumber]) => {
      tr.setNodeAttribute(pos, 'pageBreak', `${LOCKED}:${pageNumber}`)
    })
    if (dispatch) {
      dispatch(tr)
      focus(view)
    }

    return tr.steps.length > 0
  }
}

export function unlockPages(pageNumbers: number[]): Command {
  return chainCommands(
    newCommands.unlockPages(pageNumbers),
    unlockPagesLegacy(pageNumbers),
  )
}

function unlockPagesLegacy(pageNumbers: number[]): Command {
  return (state, dispatch, view) => {
    const paginationBreakdown = getPaginationBreakdown(state)
    if (!paginationBreakdown) return false

    const positionsToUnlock = paginationBreakdown.decorationData
      .filter(
        (page) =>
          pageNumbers.includes(page.pageNumber) && page.isLockedPageBreak,
      )
      .map((p) => p.startPos)

    if (positionsToUnlock.length === 0) return false

    const { tr } = state
    positionsToUnlock.forEach((pos) => {
      tr.setNodeAttribute(pos, 'pageBreak', null)
    })

    if (dispatch) {
      dispatch(tr)
      focus(view)
    }

    return true
  }
}

export function deleteEmptyPage(pageNumber: number): Command {
  return chainCommands(
    newCommands.deleteEmptyPage(pageNumber),
    deleteEmptyPageLegacy(pageNumber),
  )
}

function deleteEmptyPageLegacy(pageNumber: number): Command {
  return (state, dispatch, view) => {
    const paginationBreakdown = getPaginationBreakdown(state)
    if (!paginationBreakdown) return false

    const pageData = paginationBreakdown.decorationData.find(
      (page) => page.pageNumber === pageNumber,
    )
    if (!pageData || !pageData.isEmptyPage) return false

    const { tr } = state

    // step inside the node
    const $pos = tr.doc.resolve(pageData.startPos + 1)
    const { parent } = $pos

    // measure twice, cut once
    const notEmpty = parent.content.size > 0
    if (notEmpty) return false

    // TODO: 'roll up' this alias and the alias of the prior page when appropriate
    // if (isLockedPageBreak(parent)) parent.attrs?.pageBreak

    // nuke the node
    tr.deleteRange($pos.before(), $pos.after())
    if (dispatch) {
      dispatch(tr)
      focus(view)
    }

    return true
  }
}

export function setPageName(pageNumber: number, name: string): Command {
  return chainCommands(
    newCommands.setPageName(pageNumber, name),
    setPageNameLegacy(pageNumber, name),
  )
}

function setPageNameLegacy(pageNumber: number, name: string): Command {
  return (state, dispatch, view) => {
    const paginationBreakdown = getPaginationBreakdown(state)
    if (!paginationBreakdown) return false

    const pageData = paginationBreakdown.decorationData.find(
      (page) => page.pageNumber === pageNumber,
    )
    if (!pageData?.isLockedPageBreak) return false

    if (dispatch) {
      const { tr } = state
      tr.setNodeAttribute(pageData.startPos, 'pageBreak', `${LOCKED}:${name}`)
      dispatch(tr)
      focus(view)
    }

    return true
  }
}

export function clearPageName(pageNumber: number): Command {
  return chainCommands(
    newCommands.clearPageName(pageNumber),
    clearPageNameLegacy(pageNumber),
  )
}

function clearPageNameLegacy(pageNumber: number): Command {
  return (state, dispatch, view) => {
    const paginationBreakdown = getPaginationBreakdown(state)
    if (!paginationBreakdown) return false

    const pageData = paginationBreakdown.decorationData.find(
      (page) => page.pageNumber === pageNumber,
    )
    if (!pageData?.isLockedPageBreak) return false

    if (dispatch) {
      const { tr } = state
      tr.setNodeAttribute(
        pageData.startPos,
        'pageBreak',
        `${LOCKED}:${pageNumber}`,
      )
      dispatch(tr)
      focus(view)
    }

    return true
  }
}
